import React, { Children, useId, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import type { ElementNodeDto } from '@xshop/shared';
import { useElementRender, type ElementBoxAttrs } from './elementRenderContext';
import { textProp } from './elementProps';
import { useMenuFold } from './useMenuFold';
import './elementFoldingColumn.css';

// Sleek's footer on a phone: each column folds its links away under its heading, with a chevron that opens
// it, so the footer is a short list of headings rather than a long scroll. Wider than a phone, and inside
// the builder where every part has to stay reachable, the column is drawn as it always was.

/** What a folding column draws: its node, the box its element is styled by, and its rendered children. */
type FoldingColumnProps = { node: ElementNodeDto; box: ElementBoxAttrs; children: React.ReactNode };

// Draws a column whose first child heads it and folds the rest away on a phone.
export const FoldingColumn: React.FC<FoldingColumnProps> = ({ node, box, children }) => {
  const phone = useMenuFold('mobile');
  const { editing } = useElementRender();
  const [open, setOpen] = useState(false);
  const bodyId = useId();
  if (!phone || editing) return <div {...box}>{children}</div>;
  const [head, ...rest] = Children.toArray(children);
  const title = textProp(node.children[0]?.props ?? {}, 'text') || 'these links';
  return (
    <div {...box} data-folding={open ? 'open' : 'shut'}>
      <div className="xe-fold-row">
        {head}
        <button type="button" className="xe-fold-toggle" aria-expanded={open} aria-controls={bodyId}
          aria-label={`${open ? 'Hide' : 'Show'} ${title}`} onClick={() => setOpen(!open)}>
          <ChevronDown size={20} aria-hidden="true" />
        </button>
      </div>
      <div id={bodyId} className="xe-fold-body" hidden={!open}>{rest}</div>
    </div>
  );
};
